import { useEffect, useRef } from 'react';

import { Box, Button, Card, Heading, Paragraph } from '@zillow/constellation-10';

import { calculateGrm, compareGrm, formatGrm } from './grm';
import PropertyCard, { type PropertyState, getDisplayName } from './PropertyCard';
import {
  calculatorStyles,
  cardContentStyles,
  cardStyles,
  propertyGridStyles,
  singlePropertyStyles,
  subtleTextStyles,
} from './styles';
import useGrmUrlState from './useGrmUrlState';

const PROPERTY_A_NAME = 'Property A';
const PROPERTY_B_NAME = 'Property B';

const DEFAULT_PROPERTY_A: PropertyState = {
  name: '',
  values: { price: '250,000', rentPerUnit: '1,800', units: '1', otherIncome: '0' },
};

const DEFAULT_PROPERTY_B: PropertyState = {
  name: '',
  values: { price: '300,000', rentPerUnit: '2,200', units: '1', otherIncome: '0' },
};

const GrmCalculator = () => {
  const { propertyA, setPropertyA, propertyB, setPropertyB, isComparing, setIsComparing } = useGrmUrlState(
    DEFAULT_PROPERTY_A,
    DEFAULT_PROPERTY_B,
  );
  const propertyBNameRef = useRef<HTMLInputElement>(null);
  const shouldFocusPropertyB = useRef(false);

  const calculationA = calculateGrm(propertyA.values);
  const calculationB = calculateGrm(propertyB.values);
  const comparison =
    isComparing && calculationA.ok && calculationB.ok ? compareGrm(calculationA.result, calculationB.result) : null;

  useEffect(() => {
    if (isComparing && shouldFocusPropertyB.current) {
      shouldFocusPropertyB.current = false;
      propertyBNameRef.current?.focus();
    }
  }, [isComparing]);

  const handleToggleCompare = () => {
    if (isComparing) {
      setPropertyB(DEFAULT_PROPERTY_B);
    } else {
      shouldFocusPropertyB.current = true;
    }
    setIsComparing(!isComparing);
  };

  const getComparisonSummary = () => {
    if (!comparison) {
      return 'Enter valid values for both properties to compare them.';
    }
    if (comparison.lower === null) {
      return 'Both properties have the same GRM.';
    }
    const lowerName =
      comparison.lower === 'a'
        ? getDisplayName(propertyA, PROPERTY_A_NAME)
        : getDisplayName(propertyB, PROPERTY_B_NAME);
    return `${lowerName} has a lower GRM by ${formatGrm(comparison.difference)}. It generates more gross income relative to its price.`;
  };

  return (
    <Box css={calculatorStyles} data-testid="GrmCalculator">
      <Button tone="brand" emphasis="outlined" onClick={handleToggleCompare}>
        {isComparing ? 'Remove comparison' : 'Compare another property'}
      </Button>

      {isComparing && (
        <Card tone="neutral" outlined elevated={false} css={cardStyles}>
          <Box css={cardContentStyles}>
            <Heading level={3} textStyle="heading-sm">
              How the properties compare
            </Heading>
            <Paragraph aria-live="polite">{getComparisonSummary()}</Paragraph>
            <Paragraph css={subtleTextStyles}>
              A lower GRM isn’t a guarantee of a better investment. GRM leaves out vacancy, taxes, maintenance, and
              financing.
            </Paragraph>
          </Box>
        </Card>
      )}

      <Box css={isComparing ? propertyGridStyles : singlePropertyStyles}>
        <PropertyCard
          defaultName={PROPERTY_A_NAME}
          property={propertyA}
          calculation={calculationA}
          onChange={setPropertyA}
          showHeading={isComparing}
        />
        {isComparing && (
          <PropertyCard
            defaultName={PROPERTY_B_NAME}
            property={propertyB}
            calculation={calculationB}
            onChange={setPropertyB}
            nameInputRef={propertyBNameRef}
          />
        )}
      </Box>
    </Box>
  );
};

export default GrmCalculator;
